import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Bell, Mail, Smartphone } from 'lucide-react';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import notificationService from '../services/notificationService';
import './NotificationSettings.css';

const NotificationSettings = () => {
  const [preferences, setPreferences] = useState({
    email_notifications: true,
    sms_notifications: true,
    push_notifications: true,
    appointment_reminders: true,
    order_updates: true,
    promotional_messages: false,
    newsletter: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const navigate = useNavigate();

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      const response = await notificationService.getPreferences();
      if (response) {
        setPreferences(response);
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
      showMessage('error', 'Failed to load preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await notificationService.updatePreferences(preferences);
      showMessage('success', 'Preferences saved successfully');
    } catch (error) {
      console.error('Error saving preferences:', error);
      showMessage('error', 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="notification-settings-page">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading settings...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="notification-settings-page">
        <div className="settings-container">
          <div className="settings-header">
            <button onClick={() => navigate('/notifications')} className="back-button">
              <ArrowLeft size={20} />
            </button>
            <h1>Notification Settings</h1>
          </div>

          {message.text && (
            <div className={`message-banner ${message.type}`}>
              {message.text}
            </div>
          )}

          <div className="settings-content">
            {/* Notification Channels */}
            <div className="settings-section">
              <h2 className="section-title">
                <Bell size={20} />
                Notification Channels
              </h2>
              <p className="section-description">
                Choose how you want to receive notifications
              </p>

              <div className="settings-list">
                <div className="setting-item">
                  <div className="setting-info">
                    <Mail size={18} className="setting-icon" />
                    <div>
                      <div className="setting-label">Email Notifications</div>
                      <div className="setting-description">
                        Receive notifications via email
                      </div>
                    </div>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={preferences.email_notifications}
                      onChange={() => handleToggle('email_notifications')}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="setting-item">
                  <div className="setting-info">
                    <Smartphone size={18} className="setting-icon" />
                    <div>
                      <div className="setting-label">SMS Notifications</div>
                      <div className="setting-description">
                        Receive notifications via SMS
                      </div>
                    </div>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={preferences.sms_notifications}
                      onChange={() => handleToggle('sms_notifications')}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="setting-item">
                  <div className="setting-info">
                    <Bell size={18} className="setting-icon" />
                    <div>
                      <div className="setting-label">Push Notifications</div>
                      <div className="setting-description">
                        Receive push notifications in your browser
                      </div>
                    </div>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={preferences.push_notifications}
                      onChange={() => handleToggle('push_notifications')}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>

            {/* Notification Types */}
            <div className="settings-section">
              <h2 className="section-title">Notification Types</h2>
              <p className="section-description">
                Choose which notifications you want to receive
              </p>

              <div className="settings-list">
                <div className="setting-item">
                  <div className="setting-info">
                    <div>
                      <div className="setting-label">Appointment Reminders</div>
                      <div className="setting-description">
                        Get reminders about upcoming appointments
                      </div>
                    </div>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={preferences.appointment_reminders}
                      onChange={() => handleToggle('appointment_reminders')}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="setting-item">
                  <div className="setting-info">
                    <div>
                      <div className="setting-label">Order Updates</div>
                      <div className="setting-description">
                        Get updates about your orders and deliveries
                      </div>
                    </div>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={preferences.order_updates}
                      onChange={() => handleToggle('order_updates')}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="setting-item">
                  <div className="setting-info">
                    <div>
                      <div className="setting-label">Promotional Messages</div>
                      <div className="setting-description">
                        Receive offers and promotional content
                      </div>
                    </div>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={preferences.promotional_messages}
                      onChange={() => handleToggle('promotional_messages')}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="setting-item">
                  <div className="setting-info">
                    <div>
                      <div className="setting-label">Newsletter</div>
                      <div className="setting-description">
                        Receive our weekly health newsletter
                      </div>
                    </div>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={preferences.newsletter}
                      onChange={() => handleToggle('newsletter')}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>

            <div className="settings-actions">
              <button 
                onClick={handleSave}
                disabled={saving}
                className="save-button"
              >
                <Save size={18} />
                {saving ? 'Saving...' : 'Save Preferences'}
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default NotificationSettings;
